<template>
  <div class="code-container" @click="focusHidden()">
    <!-- Cajas visibles (solo display) -->
    <div
      v-for="(_, i) in codeLength"
      :key="i"
      class="code-box"
      :class="{ active: i === activeIndex }"
      @click.stop="setActive(i)"
    >
      {{ code[i] || '' }}
    </div>
    <!-- Input real (único), invisible pero con foco/teclado/OTP -->
    <input
      ref="hidden"
      class="hidden-input"
      type="tel"
      inputmode="numeric"
      :maxlength="codeLength"
      autocomplete="one-time-code"
      @keydown="onKeydown"
      @input="onNativeInput"
      @paste="onPaste"
    />
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, defineEmits, defineProps, watch } from 'vue';

/**
 * Este componente usa UN SOLO <input> real (oculto) para:
 *  - Permitir autocompletado de SMS (iOS/Android, navegadores modernos)
 *  - Capturar pegado de código completo
 *  - Gestionar escritura dígito a dígito y backspace
 * Las “cajas” visibles son solo representación del estado.
 */

const props = defineProps({
  length: { type: Number, default: 4 },        // largo del código
  autoFocus: { type: Boolean, default: true }, // enfocar al montar
});

const emit = defineEmits(['updateCode', 'complete']);

const codeLength = computed(() => Math.max(1, props.length));
const code = ref(Array(codeLength.value).fill(''));
const activeIndex = ref(0);
const hidden = ref(null);

const valueStr = () => code.value.join('');
const emitUpdate = () => emit('updateCode', valueStr());

// Enfoca el input oculto
const focusHidden = async () => {
  await nextTick();
  hidden.value?.focus();
  // Intenta mover el cursor “lógico” al final (no siempre respetado en móviles, pero no es crítico)
  try {
    const pos = Math.min(activeIndex.value, codeLength.value);
    hidden.value.setSelectionRange?.(pos, pos);
  } catch (_) {
    // noop
  }
};

const setActive = async (i) => {
  activeIndex.value = i;
  await focusHidden();
};

// Normaliza una cadena a solo dígitos y tamaño máximo
const onlyDigits = (str) => (str || '').replace(/\D/g, '').slice(0, codeLength.value);

// Rellena el array code[] desde una cadena
const fillFromString = (str) => {
  const digits = onlyDigits(str).split('');
  for (let i = 0; i < codeLength.value; i++) {
    code.value[i] = digits[i] || '';
  }
  // Mueve el índice activo al primer hueco o al último
  const firstEmpty = code.value.findIndex((d) => d === '');
  activeIndex.value = firstEmpty === -1 ? codeLength.value - 1 : firstEmpty;
  emitUpdate();
  if (firstEmpty === -1) emit('complete', valueStr());
};

// Escribe un dígito en la posición activa y avanza
const writeDigit = (d) => {
  if (!/^\d$/.test(d)) return;
  code.value[activeIndex.value] = d;
  emitUpdate();
  if (activeIndex.value < codeLength.value - 1) {
    activeIndex.value++;
  }
};

// Borra en la posición activa (retrocede si está vacía)
const backspace = () => {
  if (code.value[activeIndex.value]) {
    code.value[activeIndex.value] = '';
  } else if (activeIndex.value > 0) {
    activeIndex.value--;
    code.value[activeIndex.value] = '';
  }
  emitUpdate();
};

// Manejo de teclado (un solo input real)
const onKeydown = (e) => {
  const { key } = e;

  // Si no hay key (ej: autofill de SMS en iOS), dejar que onNativeInput lo maneje
  if (!key) return;

  // Pegar lo gestiona @paste/onNativeInput; evitamos duplicado
  if (key === 'v' && (e.ctrlKey || e.metaKey)) return;

  // Dígito: escribimos y prevenimos que el input agregue texto "propio"
  if (/^\d$/.test(key)) {
    e.preventDefault();
    writeDigit(key);
    return;
  }

  // Backspace
  if (key === 'Backspace') {
    e.preventDefault();
    backspace();
    return;
  }

  // Flechas izq/der para moverte entre cajas
  if (key === 'ArrowLeft') {
    e.preventDefault();
    if (activeIndex.value > 0) activeIndex.value--;
    return;
  }
  if (key === 'ArrowRight') {
    e.preventDefault();
    if (activeIndex.value < codeLength.value - 1) activeIndex.value++;
    return;
  }

  // Enter: si completo, emite 'complete'
  if (key === 'Enter') {
    if (!code.value.includes('')) emit('complete', valueStr());
    return;
  }

  // Bloquea otras teclas (espacios, letras, etc.)
  if (key.length === 1) e.preventDefault();
};

// Pegar: reparte automáticamente
const onPaste = (e) => {
  e.preventDefault();
  const text = e.clipboardData?.getData('text') ?? '';
  if (!text) return;
  fillFromString(text);
};

// Entrada nativa: captura autocompletado del sistema (SMS OTP, etc.)
const onNativeInput = (e) => {
  // Algunos navegadores (y autofill) escriben el valor completo de golpe
  const val = onlyDigits(e.target.value);
  if (!val) return;
  fillFromString(val);
};

// Mantén coherencia entre el valor del input y el array interno
watch(
  () => code.value,
  () => {
    if (hidden.value && hidden.value.value !== valueStr()) {
      hidden.value.value = valueStr();
    }
  },
  { deep: true }
);

onMounted(async () => {
  // Inicializa y opcionalmente enfoca
  hidden.value && (hidden.value.value = '');
  if (props.autoFocus) {
    await nextTick();
    activeIndex.value = 0;
    focusHidden();
  }
});
</script>

<style scoped>
.code-container {
  position: relative;
  display: flex;
  justify-content: center;
  gap: 12px;
  user-select: none;
  -webkit-user-select: none;
  margin-top: 10px;
}

/* Cajas visibles */
.code-box {
  width: 55px;
  height: 60px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  font-size: 22px;
  border-radius: 16px;
  background-color: #EDEAD5;
  border: 1px solid #5A695B;
  color: #5A695B;
  transition: box-shadow 0.15s, border-color 0.15s;
}

/* .code-box.active {
  border-color: var(--ion-color-header, #6ca270);
  box-shadow: 0 0 4px rgba(108, 162, 112, 0.4);
} */

/* Input oculto real: ocupa 0, pero recibe foco/teclado/autofill */
.hidden-input {
  position: absolute;
  top: 0;
  left: 0;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
  caret-color: transparent;
}

/* Cursor simulado en la caja activa */
.code-box.active::after {
  content: '';
  width: 2px;
  height: 60%;
  background-color: #5A695B;
  display: inline-block;
  animation: blink 1s step-start infinite;
}

@keyframes blink {
  50% { opacity: 0; }
}

.code-box.active:empty::after {
  content: '';
  width: 2px;
  height: 60%;
  background-color: #5A695B;
  display: inline-block;
  animation: blink 1s step-start infinite;
}
</style>